<template>
  <VcLoaderOverlay v-if="showLoadingSpinner" class="cart-spinner" />
  <div v-show="!isPaymentMethodExpanded && hasCheckoutCompleted" class="mb-2">
    <PaymentMethodCompleted />
  </div>
  <div v-show="props.isActiveStep && isPaymentMethodExpanded" class="payment-section md:-mx-5 md:px-6">
    <div v-if="!!creditCardFormErrorMessage" class="mb-4 mt-1 flex flex-row items-start gap-3">
      <VcIcon name="information-circle" class="error-icon-semibold"></VcIcon>
      <span v-html-safe="creditCardFormErrorMessage" class="error-text"></span>
    </div>
    <div v-if="!isOnlyQuotePaymentMethod" class="flex gap-7 leading-6">
      <div v-for="(payment, index) in actualAvailablePaymentMethods" :key="index" class="flex items-center gap-2">
        <VcRadioButton
          :id="payment.code"
          v-model="selectedPaymentMethod"
          :value="payment.code"
          name="paymentSelection"
          class="rounded-md"
          :label="getPaymentMethodLabel(payment.code)"
        >
        </VcRadioButton>
        <VcTooltip
          v-if="payment.code == 'NetTerms'"
          placement="right"
          hover
          width="max-content"
          flip-options="{ fallbackPlacements: ['top', 'bottom'] }"
        >
          <template #trigger>
            <VcIcon name="information-circle" class="" :size="18"></VcIcon>
          </template>
          <template #title>{{ $t("common.labels.invoice_me") }}</template>
          <template #content>
            {{ getTooltipText() }}
          </template>
        </VcTooltip>
      </div>
    </div>
    <div v-else>
      <div class="invoice-notif my-5 font-medium leading-6">
        {{ $t("pages.checkout.quote_payment") }}
      </div>
    </div>
    <div v-if="selectedPaymentMethodType?.code === 'CreditCard'">
      <div class="flex flex-wrap items-center gap-3 py-5 md:flex-nowrap">
        <span class="font-medium">{{ $t("shared.payment.bank_card_form.accepted_card_types_prefix") }}</span>
        <CardLabels show-type-name />
      </div>
      <PaymentProcessingCyberSource ref="paymentProcessingCyberSourceRefs" is-checkout />
      <PaymentSavedCreditCards
        ref="paymentSavedCreditCardsRefs"
        @saved-credit-cards-loaded="handleSavedCreditCardsLoaded()"
      />
    </div>

    <div v-if="selectedPaymentMethodType?.code === 'NetTerms'" class="mt-4">
      <p class="font-semibold">{{ $t("common.labels.po_reference_required") }}</p>
      <VcInput
        v-model.trim="poNumber"
        type="text"
        :maxlength="30"
        :placeholder="$t('common.labels.po_reference')"
        class="option-input mt-2.5 w-full"
      >
      </VcInput>
    </div>

    <!-- Notification Message should appear only when the invoice radio button is checked-->
    <div v-if="selectedPaymentMethodType?.code === 'NetTerms'" class="invoice-notif my-5 font-medium leading-6">
      {{ invoiceMeNotificationText }}
    </div>

    <br class="my-5" />

    <div v-if="showSameAsShipping" class="flex flex-col gap-4">
      <span class="font-bold">{{ $t("shared.checkout.billing_details_section.labels.billing_address") }}</span>
      <VcCheckbox
        id="billingAddress"
        v-model="billingAddressEqualsShipping"
        name="billingAddressEqualsShipping"
        class="rounded-md text-base font-medium"
        :label="$t('shared.checkout.billing_details_section.labels.same_as_shipping_address')"
      >
        {{ $t("shared.checkout.billing_details_section.labels.same_as_shipping_address") }}
      </VcCheckbox>
    </div>
    <div v-if="showSameAsShipping && !billingAddressEqualsShipping">
      <div class="selected-address mt-3 flex flex-col gap-1">
        <div
          v-for="(address, index) in addresses"
          :key="`billing-address-${index}`"
          class="selected-address__item flex items-start justify-between"
        >
          <VcRadioButton
            :id="address.id"
            v-model="userSelectedBillingAddress"
            name="billingAddress"
            :value="address.id!"
          >
            <ul class="flex max-w-[250px] flex-col font-medium leading-6">
              <!-- Japan-specific address format -->
              <template v-if="address.countryCode === 'JPN'">
                <li class="mb-2 font-semibold">{{ address.lastName }} {{ address.firstName }}</li>
                <li>{{ address.postalCode }}</li>
                <li>{{ address.regionName }}</li>
                <li>{{ address.city }}</li>
                <li>{{ address.line1 }}</li>
                <li v-if="address.buildingTL">{{ address.buildingTL }}</li>
                <li>{{ address.countryName }}</li>
                <li class="mt-2">
                  {{ address.phone }}
                </li>
                <li class="break-all">{{ address.email }}</li>
              </template>
              <!-- Other countries format -->
              <template v-else>
                <li v-if="address.countryCode === 'CHN'" class="mb-2 font-semibold">
                  {{ address.lastName }} {{ address.firstName }}
                </li>
                <li v-else class="mb-2 font-semibold">{{ address.firstName }} {{ address.lastName }}</li>
                <li v-if="address.organization">{{ address.organization }}</li>
                <li v-if="address.departmentTL">{{ address.departmentTL }}</li>
                <li v-if="address.buildingTL || address.mailStopTL">
                  {{ address.buildingTL ?? "" }} {{ address.mailStopTL ?? "" }}
                </li>
                <li class="break-all">
                  {{ address.line1 }}
                </li>
                <li v-if="isMUCCountry(address.countryCode!)">
                  {{ address.regionName || address.countyTL }} {{ address.postalCode }} {{ address.city }}
                </li>
                <li v-else>
                  {{ address.city }}, {{ address.regionName || address.countyTL }} {{ address.postalCode }}
                </li>
                <li>
                  {{ address.countryName }}
                </li>
                <li class="break-all">
                  {{ address.email }}
                </li>
                <li class="mb-2">
                  {{ address.phone }}
                </li>
                <li v-if="address.taxExemptNoTL" class="font-medium text-[--color-neutral-a2]">
                  {{ $t("common.labels.tax_exempt_number") }}: {{ address.taxExemptNoTL }}
                </li>
              </template>
            </ul>
          </VcRadioButton>
          <VcButton
            type="button"
            variant="link"
            size="lg"
            class="text-[--link-color] underline"
            @click="openAddOrUpdateAddressModal(address, false, true)"
          >
            {{ $t("common.buttons.edit") }}
          </VcButton>
        </div>

        <div class="flex items-center justify-between">
          <VcButton
            color="primary"
            size="lg"
            variant="link"
            @click="openAddOrUpdateAddressModal(undefined, false, true)"
          >
            <VcIcon name="plus-alt" class="mr-2" :size="16"></VcIcon>
            <span class="inline font-semibold">{{ $t("common.buttons.add_new_address") }}</span>
          </VcButton>
        </div>
      </div>
      <p v-if="!selectedBillingAddressValid" class="mt-2 text-sm text-[--color-danger]">
        {{ $t("pages.checkout.address_has_invalid_fields") }}
      </p>
      <SuggestedAddressDialog
        v-show="displaySuggestedAddressModal"
        ref="suggestedDialog"
        :suggested-address="suggestedAddress!"
        :user-typed-in-address="userTypedInAddress!"
        :existing-address-edit="existingAddressEdit!"
      />
    </div>

    <div class="mb-2 mt-3 flex w-full flex-col gap-4 md:w-fit">
      <!--TODO- this button must save and enable the next section -->
      <TlLoadingButton
        :disabled="isSaveAndReviewOrderButtonDisabled"
        color="primary"
        variant="solid"
        type="submit"
        class="mt-3 min-h-[48px]"
        size="lg"
        :complete-text="$t(`shared.wishlists.list_card.saved`)"
        @click="nextStepHandle"
      >
        {{ $t("pages.checkout.save_and_review") }}
      </TlLoadingButton>
    </div>
  </div>

  <!-- Step-Up Authentication Overlay -->
  <StepUpAuthenticationOverlay />
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ValidationError } from "yup";
import { logClientInformation } from "@/core/api/graphql";
import { useCountriesWithoutEmbargoed, useCurrency, useDataAreaId } from "@/core/composables";
import { globals } from "@/core/globals";
import { isEqualAddresses, isMUCCountry } from "@/core/utilities/address";
import { getInvoiceMeNotificationText } from "@/core/utilities/orders";
import { useUser, useUserPayments } from "@/shared/account";
import { useAddressModal } from "@/shared/account/composables/useAddressModal";
import { useChannelPartner } from "@/shared/account/composables/useChannelPartner";
import {
  analyzeCyberSourceError,
  getCyberSourceErrorMessage,
  shouldReinitPaymentForm,
} from "@/shared/payment/utils/cybersource-error-handler";
import { eventBus } from "@/shared/thorlabs/shared";
import { createAddressObjectSchema } from "@/ui-kit/components/molecules/address-form/addressValidationSchemas";
import { useCheckoutFacade } from "../../composables/useCheckoutFacade";
import { currencyConfig } from "../../currency-settings";
import PaymentMethodCompleted from "./payment-completed.vue";
import type { CurrencySettingsType, CheckoutPaymentMethodType } from "../../types";
import type { MemberAddressTypeExtension } from "@/core/api/graphql/types";
import SuggestedAddressDialog from "@/shared/account/components/suggested-address-dialog.vue";
import StepUpAuthenticationOverlay from "@/shared/checkout/components/step-up-authentication-overlay.vue";
import CardLabels from "@/shared/payment/components/card-labels.vue";
import PaymentProcessingCyberSource from "@/shared/payment/components/payment-processing-cyber-source.vue";
import PaymentSavedCreditCards from "@/shared/payment/components/payment-saved-credit-cards.vue";
import TlLoadingButton from "@/ui-kit/components/molecules/button/tl-loading-button.vue";

interface IProps {
  isCompletedStep?: boolean;
  isActiveStep?: boolean;
  nextStep?: string;
}
const props = defineProps<IProps>();
const { user } = useUser();
const { t } = useI18n();
const { countriesWithoutEmbargoed } = useCountriesWithoutEmbargoed();

const selectedBillingAddressValid = ref(true);

const billingAddressSchema = computed(() =>
  createAddressObjectSchema({
    t,
    withPersonalInfo: true,
    requiredEmail: true,
    requiredPhone: true,
    requiredCity: true,
    countries: countriesWithoutEmbargoed.value,
  }),
);
const {
  availablePaymentMethods,
  deliveryAddress,
  setCartDeliveryAddress,
  isPaymentMethodExpanded,
  hasCheckoutCompleted,
  addresses,
  billingAddress,
  userSelectedAddress,
  billingAddressEqualsShipping,
  poNumber,
  isPORequired,
  isPORequiredForQuote,
  selectedPaymentMethod,
  nextStepHandle: nextStepHandleFacade,
  userSelectedBillingAddress,
  createOrder,
  authorizationParameters,
  stopLoading,
} = useCheckoutFacade();
const { payments } = useUserPayments();
const { setBillingCountryCode, getDataAreaId, setSelectedPaymentMethodForDataAreaId } = useDataAreaId();
const { currentCurrency } = useCurrency();
const { isChannelPartner } = useChannelPartner();
const showLoadingSpinner = ref(false);
const creditCardFormErrorMessage = ref("");
const displaySuggestedAddressModal = ref(false);
const suggestedDialog = ref<InstanceType<typeof SuggestedAddressDialog> | null>(null);
const paymentProcessingCyberSourceRefs = ref<InstanceType<typeof PaymentProcessingCyberSource> | null>(null);
const paymentSavedCreditCardsRefs = ref<InstanceType<typeof PaymentSavedCreditCards> | null>(null);
const actualAvailablePaymentMethods = ref<CheckoutPaymentMethodType[]>([]);
const isOnlyQuotePaymentMethod = computed(() => {
  return actualAvailablePaymentMethods.value.length === 1 && actualAvailablePaymentMethods.value[0].code === "Quote";
});
const isSaveAndReviewOrderButtonDisabled = computed(() => {
  if (!selectedPaymentMethodType.value || isPORequired.value || isPORequiredForQuote.value) {
    return true; // If no payment method is selected, the button is disabled
  }

  if (selectedPaymentMethodType.value?.code === "Quote" || selectedPaymentMethodType.value?.code === "NetTerms") {
    return !userSelectedBillingAddress.value && !billingAddressEqualsShipping.value;
  }

  if (selectedPaymentMethodType.value?.code === "CreditCard") {
    // If using a saved card
    if (paymentSavedCreditCardsRefs.value?.selectedCard) {
      // Check if the selected card has a valid billing address
      return !paymentSavedCreditCardsRefs.value?.isSelectedCardValid;
    }
    // If adding a new card
    return (
      (!userSelectedBillingAddress.value && !billingAddressEqualsShipping.value) ||
      !paymentProcessingCyberSourceRefs?.value?.isValidBankCard
    );
  }

  return false; // If a payment method is selected and conditions are met, the button is enabled
});
const selectedPaymentMethodType = computed<CheckoutPaymentMethodType | undefined>(() => {
  if (isOnlyQuotePaymentMethod.value) {
    // If only Quote payment method is available, use it automatically
    return availablePaymentMethods.value.find((method) => method.code === "Quote");
  } else if (isOnlyNetTermsPaymentMethod.value) {
    return availablePaymentMethods.value.find((method) => method.code === "NetTerms");
  }
  return availablePaymentMethods.value.find((method) => method.code === selectedPaymentMethod.value);
});

const currentBillingAddressCountryCode = ref("");
const showPaymentForm = ref(false);
const isOnlyNetTermsPaymentMethod = computed(() => {
  const onlyNetTerms =
    actualAvailablePaymentMethods.value.length === 1 && actualAvailablePaymentMethods.value[0].code === "NetTerms";

  if (onlyNetTerms) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    selectedPaymentMethod.value = "NetTerms";
  }

  return onlyNetTerms;
});

const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;

const showSameAsShipping = computed(() => {
  return !(selectedPaymentMethodType.value?.code === "CreditCard" && !showPaymentForm.value);
});

const showDialog = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  displaySuggestedAddressModal.value = true;
  suggestedDialog.value?.showDialog();
};
const { suggestedAddress, userTypedInAddress, existingAddressEdit, openAddOrUpdateAddressModal } =
  useAddressModal(showDialog);

const nextStepHandle = async (callback: (result: unknown) => void) => {
  if (!selectedPaymentMethodType.value) {
    return;
  }

  await nextStepHandleFacade(
    callback,
    selectedPaymentMethodType.value.code,
    false,
    paymentSavedCreditCardsRefs.value?.selectedCard?.cardDetails ?? null,
    paymentSavedCreditCardsRefs.value?.getParameters ?? null,
    paymentProcessingCyberSourceRefs.value?.createTokenWithValidationPayload ?? null,
    paymentProcessingCyberSourceRefs.value?.setAsDefaultPaymentCard ?? null,
    paymentProcessingCyberSourceRefs.value?.getParameters ?? null,
  );
};

const handlePaymentError = (payload: { reason: string }) => {
  const errorResult = analyzeCyberSourceError(payload);
  const errorMsg = getCyberSourceErrorMessage(
    errorResult,
    t,
    "shared.payment.bank_card_form.default_authorization_error_checkout",
  );
  const hasPaymentToken = !!authorizationParameters.value?.find((x) => x.key === "paymentInstrumentTokenId");

  eventBus.emit("expandCheckoutComponent", {
    initPaymentForm: shouldReinitPaymentForm(errorResult) ? !hasPaymentToken : false,
  });
  eventBus.emit("showCreditCardFormError", { message: errorMsg });
  stopLoading();
};

const closeModal = () => {
  eventBus.emit("closeStepUpAuthentication");
};

const stepUpReturnUrlOrigin = ref<string | null>(null);
const stepUpAuthTransactionId = ref<string | undefined>(undefined);

interface IThreeDSMessageData {
  type?: string;
}

const handle3DSComplete = async (event: MessageEvent) => {
  if (!stepUpReturnUrlOrigin.value) {
    // Many unrelated postMessage events (browser extensions, iframes, etc.) fire here; not worth logging.
    return;
  }

  const data = event.data as IThreeDSMessageData | null | undefined;
  // Only messages that actually claim to be our 3DS completion are worth inspecting/logging further;
  // other frames/extensions send unrelated postMessage events constantly.
  if (data?.type !== "3DS_COMPLETE") {
    return;
  }

  if (event.origin !== stepUpReturnUrlOrigin.value) {
    void logClientInformation({
      title: "StepUpAuthentication.originMismatch",
      content: `postMessage origin mismatch. expected=${stepUpReturnUrlOrigin.value}, actual=${event.origin}, authenticationTransactionId=${stepUpAuthTransactionId.value ?? "unknown"}`,
      withTelemetry: true,
      userId: user.value?.memberId ?? user.value?.id ?? "unknown",
    }).catch(() => {});
    return;
  }

  void logClientInformation({
    title: "StepUpAuthentication.challengeCompleted",
    content: `3DS_COMPLETE message received. authenticationTransactionId=${stepUpAuthTransactionId.value ?? "unknown"}`,
    withTelemetry: true,
    userId: user.value?.memberId ?? user.value?.id ?? "unknown",
  }).catch(() => {});
  closeModal();
  await createOrder(() => {});
};

const setUserSelectedBillingAddress = () => {
  if (!addresses.value.length) {
    return;
  }

  let selectedId: string | undefined;

  if (billingAddress.value) {
    const autoSelectedAddress = addresses.value.find((item) =>
      isEqualAddresses(item, Object.assign({}, billingAddress.value)),
    );
    if (autoSelectedAddress?.id) {
      selectedId = autoSelectedAddress.id;
    }
  }

  if (!selectedId) {
    const defaultBillingAddress = addresses.value.find((address) => address.isDefaultBillingAddressTL);
    if (defaultBillingAddress?.id) {
      selectedId = defaultBillingAddress.id;
    }
  }

  if (!selectedId) {
    const mostRecentAddress = addresses.value.reduce(
      (latest: MemberAddressTypeExtension, address: MemberAddressTypeExtension) => {
        return new Date(address.modifiedDate) > new Date(latest.modifiedDate) ? address : latest;
      },
      addresses.value[0],
    );
    if (mostRecentAddress?.id) {
      selectedId = mostRecentAddress.id;
    }
  }

  if (selectedId) {
    userSelectedBillingAddress.value = selectedId;
  }
};

const userSelectedBillingAddressDisplay = computed(() => {
  return addresses.value.find((address) => address.id === userSelectedBillingAddress.value);
});

watch(
  userSelectedBillingAddressDisplay,
  async (newAddress?: MemberAddressTypeExtension) => {
    if (!newAddress || billingAddressEqualsShipping.value) {
      selectedBillingAddressValid.value = true;
      return;
    }
    try {
      await billingAddressSchema.value.validate(newAddress, { abortEarly: true });
      selectedBillingAddressValid.value = true;
    } catch (error) {
      selectedBillingAddressValid.value = !(error instanceof ValidationError);
    }
  },
  { immediate: true },
);

const onShowStepUpAuthentication = (payload: {
  accessToken: string;
  stepUpUrl: string;
  returnUrl?: string;
  authenticationTransactionId?: string;
}) => {
  stepUpAuthTransactionId.value = payload.authenticationTransactionId;
  if (payload.returnUrl) {
    try {
      stepUpReturnUrlOrigin.value = new URL(payload.returnUrl).origin;
    } catch {
      stepUpReturnUrlOrigin.value = null;
    }
  } else {
    stepUpReturnUrlOrigin.value = null;
  }
};

const onCloseStepUpAuthentication = () => {
  stepUpReturnUrlOrigin.value = null;
};

eventBus.on("expandCheckoutComponent", (payload) => {
  isPaymentMethodExpanded.value = true;
  hasCheckoutCompleted.value = false;
  if (payload.initPaymentForm) {
    paymentProcessingCyberSourceRefs.value?.loadPaymentForm(
      getDataAreaId(currentCurrency.value.code, currentBillingAddressCountryCode.value),
    ) ?? null;
  }
});
eventBus.on("showStepUpAuthentication", onShowStepUpAuthentication);
eventBus.on("closeStepUpAuthentication", onCloseStepUpAuthentication);
eventBus.on("showCreditCardFormError", (payload) => {
  creditCardFormErrorMessage.value = payload.message;
  // Scroll to top of the payment step to show the error message
  const paymentStepElement = document.querySelector(".payment-section");
  paymentStepElement?.scrollIntoView({ behavior: "smooth" });
});
eventBus.on("hideCreditCardFormError", () => {
  creditCardFormErrorMessage.value = "";
});

onUnmounted(() => {
  creditCardFormErrorMessage.value = "";
  eventBus.off("expandCheckoutComponent");
  eventBus.off("showCreditCardFormError");
  eventBus.off("hideCreditCardFormError");
  eventBus.off("showStepUpAuthentication", onShowStepUpAuthentication);
  eventBus.off("closeStepUpAuthentication", onCloseStepUpAuthentication);
});

onMounted(() => {
  eventBus.on("paymentError", handlePaymentError);
  window.addEventListener("message", handle3DSComplete);

  if (location.pathname.includes("checkout") || location.pathname.includes("acceptQuote")) {
    actualAvailablePaymentMethods.value = availablePaymentMethods.value.filter((method) => {
      // Channel partners use Invoice Me only (CC deferred until JPY CC is accepted)
      if (isChannelPartner.value && method.code === "CreditCard") {
        return false;
      }
      return method.code !== "Quote" || !(currencySettings?.availablePaymentMethods?.includes(method.code) ?? false);
    });
    // Channel partners can't use CreditCard, so default to Invoice Me (NetTerms); fall back to the
    // first available method so checkout never ends up with no payment method selected.
    const preferredPaymentMethodCode = isChannelPartner.value ? "NetTerms" : "CreditCard";
    selectedPaymentMethod.value =
      actualAvailablePaymentMethods.value.find((method) => method.code === preferredPaymentMethodCode)?.code ??
      actualAvailablePaymentMethods.value[0]?.code ??
      "";
  } else {
    actualAvailablePaymentMethods.value = availablePaymentMethods.value.filter((method) => {
      return method.code === "Quote";
    });
    // Set correct selectedPaymentMethod for haiving the correct login in getDataAreaId
    setSelectedPaymentMethodForDataAreaId(selectedPaymentMethod.value);
  }

  if (selectedPaymentMethod.value !== "CreditCard") {
    setUserSelectedBillingAddress();
  }
});

onBeforeUnmount(() => {
  eventBus.off("paymentError", handlePaymentError);
  window.removeEventListener("message", handle3DSComplete);
});

watch(
  () => props.isActiveStep,
  (newVal) => {
    isPaymentMethodExpanded.value = newVal;
  },
);

watch(
  [
    selectedPaymentMethodType,
    billingAddressEqualsShipping,
    deliveryAddress,
    userSelectedBillingAddress,
    addresses,
    () => paymentSavedCreditCardsRefs.value?.selectedCard,
    () => showPaymentForm.value,
  ],
  () => {
    let result = "";
    if (selectedPaymentMethodType.value) {
      const isEligibleForAddressSelection =
        selectedPaymentMethodType.value.code !== "CreditCard" ||
        (selectedPaymentMethodType.value.code === "CreditCard" && showPaymentForm.value);
      if (isEligibleForAddressSelection) {
        if (billingAddressEqualsShipping.value) {
          result = deliveryAddress.value?.countryCode ?? "";
        } else if (userSelectedBillingAddress.value) {
          const selectedAddress = addresses.value.find((address) => address.id === userSelectedBillingAddress.value);
          result = selectedAddress?.countryCode ?? "";
        }
      } else if (
        paymentSavedCreditCardsRefs.value?.selectedCard &&
        paymentSavedCreditCardsRefs.value?.selectedCard.cardDetails.billingAddress
      ) {
        result = paymentSavedCreditCardsRefs.value?.selectedCard.cardDetails.billingAddress?.countryCode ?? "";
      }
    }
    currentBillingAddressCountryCode.value = result;
  },
  { immediate: true },
);

watch([currentBillingAddressCountryCode, selectedPaymentMethod], async () => {
  const newBillingDataAreaId = getDataAreaId(currentCurrency.value.code, currentBillingAddressCountryCode.value);
  const currentDataAreaId = paymentProcessingCyberSourceRefs.value?.currentDataAreaId;
  if (!!currentBillingAddressCountryCode.value && newBillingDataAreaId !== currentDataAreaId) {
    setBillingCountryCode(currentBillingAddressCountryCode.value);
    if (!payments.value?.length && selectedPaymentMethodType.value?.code === "CreditCard") {
      if (currentDataAreaId) {
        eventBus.emit("showCreditCardFormError", {
          message: t("shared.payment.bank_card_form.daid_changed"),
        });
      }
      // Wait for the v-if to re-render the CyberSource component before accessing its ref
      await nextTick();
      paymentProcessingCyberSourceRefs.value?.loadPaymentForm(newBillingDataAreaId);
    }
  }
});

// Auto-select billing address when addresses become available but none is selected yet.
// This covers the case where the user enters checkout with no saved addresses:
// handleSavedCreditCardsLoaded calls setUserSelectedBillingAddress when addresses is still empty,
// so userSelectedBillingAddress stays unset. When the user later adds an address in the shipping
// step, this watcher ensures a billing address gets auto-selected, allowing the
// currentBillingAddressCountryCode watcher chain to resolve and trigger loadPaymentForm.
watch(addresses, (newAddresses) => {
  if (
    newAddresses.length > 0 &&
    showPaymentForm.value &&
    !userSelectedBillingAddress.value &&
    selectedPaymentMethodType.value?.code === "CreditCard"
  ) {
    setUserSelectedBillingAddress();
  }
});

watch(selectedPaymentMethod, async (newMethod) => {
  const selectedAddress =
    addresses.value.find((address) => address.id === userSelectedAddress.value) ??
    addresses.value.find((item) => isEqualAddresses(item, Object.assign({}, deliveryAddress.value)));
  if (selectedAddress) {
    setSelectedPaymentMethodForDataAreaId(selectedPaymentMethod.value);
    await setCartDeliveryAddress(selectedAddress, newMethod);
  }
});

const invoiceMeNotificationText = computed(() => {
  const netTerms = user.value?.contact?.organizations?.items?.[0]?.netTerms ?? "";

  return getInvoiceMeNotificationText(netTerms, t);
});

function getTooltipText() {
  const netTerms = user.value?.contact?.organizations?.items?.[0]?.netTerms ?? "";

  return getInvoiceMeNotificationText(netTerms, t, true);
}

function getPaymentMethodLabel(paymentMethodCode: string) {
  if (paymentMethodCode === "CreditCard") {
    return t("common.labels.credit_card");
  } else if (paymentMethodCode === "NetTerms") {
    return t("common.labels.invoice_me");
  }
  return paymentMethodCode;
}

function handleSavedCreditCardsLoaded() {
  if (!payments.value?.length && !userSelectedBillingAddress.value) {
    setUserSelectedBillingAddress();
    showPaymentForm.value = true;
  }
}
</script>

<style lang="scss">
.payment-section {
  .vc-radio-button {
    &.vc-radio-button--checked {
      .vc-radio-button__text {
        @apply font-bold text-[--color-neutral-a1];
      }
    }
  }
}
.error-icon-semibold {
  @apply font-semibold text-[--color-danger-450];
}
.error-text {
  @apply text-[--color-danger-450];
}
</style>
